// packages/core/src/learning/schools/adulting-life-skills.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored to match boglehead-indexing.ts (the reference exhibit).
//
// Voice reminder: neutral enough to be trustworthy, opinionated enough to be
// memorable. Frame beliefs as "[School] believes…". Never invent quotes,
// never tell users what to buy, never copy book/creator text.
//
// ID-bound fields (deferred to the post-authoring cross-reference pass):
//   - figures:            [] (none for this school)
//   - featuredLessonIds:  [] until referenced lessons exist
//   - debateIds:          [] until referenced debates exist
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const ADULTING_LIFE_SKILLS_EXHIBIT: SchoolExhibitEntry = {
	id: 'adulting-life-skills',
	tagline:
		'Adulting life skills believes competence lowers the cost of chaos.',
	worldviewSummary:
		'Adulting life skills is paying attention to the boring systems that quietly run your life: rent, bills, documents, contracts, cars, insurance, deadlines, and basic household decisions. This lens protects you from expensive confusion, missed details, and panic decisions that happen when real life moves faster than your preparation. It accepts a practical tradeoff: competence is not glamorous, and it can feel slow compared with chasing bigger money moves. The blind spot is that adulting can become endless maintenance if you never connect it back to freedom, confidence, and better choices.',
	coreBeliefs: [
		'Adulting life skills believes basic competence is a form of financial protection.',
		'Adulting life skills believes the details you ignore can become the fees you pay later.',
		'Adulting life skills believes contracts, bills, insurance, and documents deserve real attention before there is a crisis.',
		'Adulting life skills believes money confidence grows when your life systems are visible instead of scattered.',
		'Adulting life skills believes independence is built through repeatable habits, not one heroic cleanup day.',
		'Adulting life skills believes boring preparation can buy calmer decisions later.',
	],
	warnsAgainst: [
		'Treating rent, bills, insurance, and paperwork as background noise until something breaks.',
		'Signing contracts you do not understand because you feel rushed or embarrassed.',
		'Confusing independence with doing everything alone and unprepared.',
		'Letting small missed deadlines turn into fees, stress, damaged credit, or avoidable conflict.',
	],
	helps: 'This lens helps adults who want to feel more capable handling rent, cars, bills, documents, insurance, and everyday money pressure. It may not fit someone looking for advanced investing theory, because its power is basic competence, not clever optimization.',
	figures: [],
	canon: [
		{
			title: 'The life admin mindset',
			oneLineIdea:
				'Your money life gets easier when bills, documents, deadlines, and decisions have a home.',
		},
		{
			title: 'Read before you sign',
			oneLineIdea:
				'A contract is not just paperwork. It is a future obligation with rules attached.',
		},
		{
			title: 'Runway before pride',
			oneLineIdea:
				'Independence works better when you can cover the boring costs that come with it.',
		},
		{
			title: 'Insurance as downside control',
			oneLineIdea:
				'Insurance is not exciting, but it can stop one bad event from becoming a financial spiral.',
		},
		{
			title: 'Maintenance beats emergencies',
			oneLineIdea:
				'Small checks done early can prevent expensive fixes later.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-adulting-life-skills',
	debateIds: [],
	applyActions: [
		{
			label: 'Check how much runway protects your next big move',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Compare a life decision against your real numbers',
			action: {
				type: 'openSimulator',
				label: 'Simulate the decision',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Look at your current money picture before adding obligations',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Write down the bills and documents you cannot afford to lose track of',
			action: {
				type: 'noToolAction',
				label: 'Map your life admin',
				prompt: 'List your recurring bills, key documents, contract dates, and renewal deadlines in one place.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Adulting is not paperwork. It is how you stop small mistakes from taxing your future.',
			kind: 'hook',
		},
		{
			line: 'The most expensive money lesson might be the contract you signed without reading.',
			kind: 'hook',
		},
		{
			line: 'What bill, fee, or document mistake taught you to take adulting seriously?',
			kind: 'interview-question',
		},
		{
			line: 'If you had to start over today, what basic life system would you set up first?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches the unsexy money skills that protect real independence.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Learn the life admin most people only respect after it costs them money.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how one missed bill, rushed contract, or bad car decision can shrink future options.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people what adult responsibility surprised them most once they moved out.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
